import React from "react";
import { Box, Paper, Typography, Button, Stack, Divider } from "@mui/material";
import type { TypographyProps } from '@mui/material';
import { styled } from "@mui/material/styles";

/**
 * Shared Authentication Components
 *
 * These components provide consistent styling and layout
 * for all authentication forms and pages.
 */

// Auth Layout - Main container for auth pages
export const AuthLayout = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(2),
}));

// Auth Panel - Card container for auth forms
export const AuthPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 400,
  width: "100%",
  borderRadius: 16,
  boxShadow: theme.shadows[3],
}));

// Auth Management Layout - For profile/settings pages
export const AuthManageLayout = styled(Box)(({ theme }) => ({
  maxWidth: 720,
  minWidth: 0,
  margin: "0 auto",
  padding: theme.spacing(2, 0),
  overflowWrap: 'anywhere',
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(3, 0) },
}));

// Auth Headers
export const AuthH1: React.FC<TypographyProps> = (props) => (
  <Typography component="h1" variant="h4" fontWeight={600} textAlign="center" mb={2} color="text.primary" {...props} />
);

export const AuthH2: React.FC<TypographyProps> = (props) => (
  <Typography component="h2" variant="h5" fontWeight={500} mb={2} color="text.primary" {...props} />
);

// Auth Button - Primary action button
export const AuthButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  fontSize: "1rem",
  fontWeight: 500,
  textTransform: "none",
}));

// Auth Fields - Container for form fields
export const AuthFields = styled(Stack)(({ theme }) => ({
  spacing: 2,
  "& .MuiTextField-root": {
    marginBottom: theme.spacing(2),
  },
}));

// Social Provider Types and Configuration
export interface SocialProvider {
  id: string;
  name: string;
  icon?: React.ReactNode;
  color?: string;
}

export const AVAILABLE_PROVIDERS: SocialProvider[] = [
  {
    id: "google",
    name: "Google",
    color: "#4285f4",
  },
  {
    id: "github",
    name: "GitHub",
    color: "#333",
  },
  {
    id: "microsoft",
    name: "Microsoft",
    color: "#00a4ef",
  },
];

// Social Provider List Component
interface SocialProviderListProps {
  providers: SocialProvider[];
  process: "login" | "signup" | "connect";
  isLoading: boolean;
  loadingProvider: string | null;
  onProviderClick: (provider: SocialProvider) => void;
}

export const SocialProviderList: React.FC<SocialProviderListProps> = ({
  providers,
  process,
  isLoading,
  loadingProvider,
  onProviderClick,
}) => {
  return (
    <Stack spacing={2}>
      {providers.map((provider) => (
        <Button
          key={provider.id}
          variant="outlined"
          fullWidth
          onClick={() => onProviderClick(provider)}
          disabled={isLoading}
          sx={{
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
            borderColor: "readable.link",
            color: "text.primary",
            "&:hover": {
              borderColor: "readable.link",
              backgroundColor: provider.color
                ? `${provider.color}10`
                : "action.hover",
            },
          }}
        >
          {loadingProvider === provider.id
            ? "Connecting..."
            : `${process === "connect" ? "Connect" : "Continue with"} ${provider.name}`}
        </Button>
      ))}
    </Stack>
  );
};

// Social Provider Button - Individual social auth button
interface SocialProviderButtonProps {
  provider: SocialProvider;
  process: "login" | "signup" | "connect";
  isLoading?: boolean;
  onClick: () => void;
}

export const SocialProviderButton: React.FC<SocialProviderButtonProps> = ({
  provider,
  process,
  isLoading = false,
  onClick,
}) => {
  return (
    <Button
      variant="outlined"
      fullWidth
      onClick={onClick}
      disabled={isLoading}
      sx={{
        py: 1.5,
        textTransform: "none",
        fontWeight: 500,
        borderColor: "readable.link",
        color: "text.primary",
        "&:hover": {
          borderColor: "readable.link",
          backgroundColor: provider.color
            ? `${provider.color}10`
            : "action.hover",
        },
      }}
    >
      {isLoading
        ? "Connecting..."
        : `${process === "connect" ? "Connect" : "Continue with"} ${provider.name}`}
    </Button>
  );
};

// Form Section Divider
export const FormDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(3, 0),
  "&::before, &::after": {
    borderColor: theme.palette.divider,
  },
}));

// Loading Overlay for auth actions
export const AuthLoadingOverlay: React.FC<{
  isLoading: boolean;
  children: React.ReactNode;
}> = ({ isLoading, children }) => {
  return (
    <Box sx={{ position: "relative" }}>
      {children}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        />
      )}
    </Box>
  );
};
